<template>
  <div ref="root" :class="['ToastStack', `ToastStack__${position}`]">
    <TransitionGroup name="ToastStack__item">
      <UIToast
        v-for="toast in toastStore.toasts"
        :key="toast.id"
        :class="['ToastStack__item', `ToastStack__item-${toast.type}`]"
        v-bind="
          toast.type === 'error'
            ? { role: 'alert', 'aria-live': 'assertive' }
            : {}
        "
        :theme="FEEDBACK_THEME_MAP[toast.type].theme"
        :icon="FEEDBACK_THEME_MAP[toast.type].icon"
        :title="toast.title || toPlainText(toast.message)"
        titleTag="h5"
        :description="toast.title ? toPlainText(toast.message) : undefined"
        @dismiss="toastStore.removeToast(toast.id)"
      />
    </TransitionGroup>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUpdated, ref, watch } from 'vue'
import type { PropType } from 'vue'

import { UIToast } from '@foundever/ui-lib'

import { useToast } from '@/store/toast'

import {
  FEEDBACK_THEME_MAP,
  TOAST_POSITION,
  labelDismissButtons,
  toPlainText,
} from './types'

const toastStore = useToast()

const root = ref<HTMLElement | null>(null)
onMounted(() => labelDismissButtons(root.value))
onUpdated(() => labelDismissButtons(root.value))
// Items render inside TransitionGroup's slot, so the stack itself does not
// re-render when they change; relabel after the DOM update as well.
watch(
  () => toastStore.toasts.map((t) => t.id).join(','),
  () => labelDismissButtons(root.value),
  { flush: 'post' },
)

defineProps({
  position: {
    type: String as PropType<TOAST_POSITION>,
    default: TOAST_POSITION.TOP_CENTER,
  },
})
</script>

<style lang="scss" scoped>
.ToastStack {
  z-index: 2000;
  position: fixed;
  padding: 1rem;
  max-width: 43rem;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  pointer-events: none;

  &__item {
    pointer-events: auto;
    box-sizing: border-box;

    :deep(.UIAlert__container) {
      min-width: 0;
    }
    :deep(.UIAlert__title),
    :deep(.UIAlert__description) {
      overflow-wrap: anywhere;
    }

    // ToastStack item animation
    &-move,
    &-enter-active,
    &-leave-active {
      transition: all 0.5s ease;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateX(1.5rem);
    }
  }

  &__top-right {
    top: 0;
    right: 0;
    align-items: flex-end;
  }

  &__top-left {
    top: 0;
    left: 0;
    align-items: flex-start;
  }

  &__top-center {
    top: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    align-items: center;
  }

  &__bottom-left {
    bottom: 0;
    left: 0;
    align-items: flex-start;
  }

  &__bottom-right {
    bottom: 0;
    left: 0;
    align-items: flex-start;
  }
}
</style>
